import { useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './RichTextEditor.css';

/**
 * RichTextEditor - A WYSIWYG editor component using React-Quill
 *
 * @param {Object} props
 * @param {string} props.value - The HTML content value
 * @param {function} props.onChange - Callback when content changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional CSS class names
 * @param {boolean} props.readOnly - Whether the editor is read-only
 * @param {number} props.minHeight - Minimum height in pixels (default: 300)
 */
function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Write your content here...',
  className = '',
  readOnly = false,
  minHeight = 300,
}) {
  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, 4, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link', 'image'],
        ['blockquote', 'code-block'],
        ['clean'],
      ],
    },
    clipboard: {
      matchVisual: false,
    },
  }), []);

  // Quill formats configuration
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'link', 'image',
    'blockquote', 'code-block',
  ];

  const handleChange = (content, delta, source, editor) => {
    // Get plain HTML content
    const html = editor.getHTML();
    // Pass HTML to parent component
    if (onChange) {
      onChange(html === '<p><br></p>' ? '' : html);
    }
  };

  return (
    <div
      className={`rich-text-editor ${className}`}
      style={{ '--editor-min-height': `${minHeight}px` }}
    >
      <ReactQuill
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly}
      />
    </div>
  );
}

export default RichTextEditor;
